import { z } from "zod";
import { RemittanceDocSchema } from "./remittance-schemas";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";

/**
 * Build Remittance Verification Subject
 *
 * Adapter that assembles the generic `verify_step_output` inputs for a
 * remittance advice: the extracted fields + line items as a plain `subject`,
 * the source `documentIds` for multimodal grounding, and the entity link
 * (`AR_REMITTANCE_ADVICE` + adviceId) so a failed check's escalation task is
 * attached to the remittance.
 *
 * The source document is taken from the live `document` reference passed in
 * (wired from `normalize_remittance_document.document`) and flattened to
 * `documentIds` — keeping the generic verify node's surface to a single
 * `documentIds` input rather than teaching it about document references.
 *
 * **The subject is the extraction, not the resolution.** What this check is
 * for is extraction quality — do all the lines carry an amount, do they sum to
 * the stated total, did the model hallucinate a duplicate row — so the subject
 * is built from `remittanceDoc`, the payload `normalize_remittance_document`
 * read out of the OCR result. Reading the persisted `ar_remittance_line_items`
 * instead would check a set that `resolve_invoice_references` has already
 * reinterpreted: it stores a withheld line's amount as its absolute value and
 * appends standalone deduction rows that were never invoice references, so the
 * sum-to-total criterion no longer holds against the document the verifier is
 * grounded on.
 *
 * Sourcing the subject upstream of the resolver also decouples the two: this
 * step needs nothing that `resolve_invoice_references` writes, so it needs no
 * ordering edge against it and can flag a bad extraction before that extraction
 * has produced line items or collection threads. The Payment is already in
 * place — the advice id this step links its escalation to comes from
 * `create_pending_payment`, which is a hard prerequisite.
 */
export const buildRemittanceVerificationSubjectStep = defineStepConfig({
  type: "build_remittance_verification_subject",
  name: "Build Remittance Verification Subject",
  description:
    "Assemble the verify_step_output inputs (subject + documentIds + entity link) for a remittance advice.",
  category: "Data Access",
  libraryModule: "AccountsReceivable",
  inputSchema: z.object({
    remittanceAdviceId: z
      .uuid()
      .describe("Remittance advice to assemble a verification subject for"),
    document: entityOfType(SystemEntity.CORE_DOCUMENT)
      .optional()
      .describe(
        "Source document to verify against (normalize_remittance_document passthrough); flattened to documentIds. Empty when the advice has no attached document.",
      ),
    remittanceDoc: RemittanceDocSchema.optional().describe(
      "The extracted remittance payload to verify (normalize_remittance_document passthrough). Optional only so workflow definitions wired before this input existed keep running: without it the subject is rebuilt from the persisted line items, which is what this step used to do.",
    ),
  }),
  outputSchema: z.object({
    subject: z
      .record(z.string(), z.unknown())
      .describe(
        "Extracted remittance fields + line items to sanity-check, as read off the document",
      ),
    documentIds: z
      .array(z.uuid())
      .describe(
        "Source document ids for multimodal grounding (empty when none); fed to verify_step_output.documentIds",
      ),
    sourceEntityType: z
      .nativeEnum(SystemEntity)
      .describe(
        "Entity type the escalation task links to (AR_REMITTANCE_ADVICE)",
      ),
    sourceEntityId: z
      .uuid()
      .describe("Entity id the escalation task links to (the advice id)"),
  }),
  configSchema: z.object({}),
  emits: [] as const,
});
